import React, { FC } from 'react';
import { Field, Form } from 'react-final-form';
import {
  composeValidators,
  isEmail,
  isPhoneNumber,
  isRequired,
} from '../../../shared';
import styles from './createHospitalForm.module.scss';
import { useTranslation } from 'react-i18next';
import { ICreateHospitalForm } from './createHospitalForm.interface';
import { TimePickerField } from '../../FormFields';

export const CreateHospitalForm: FC<ICreateHospitalForm> = ({
  onSubmit,
  initialValues,
  disabled,
}) => {
  const { t } = useTranslation();

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={initialValues}
      render={({ handleSubmit, form, submitting, pristine, values }) => (
        <form
          onSubmit={handleSubmit}
          className={`${disabled && styles.disabled} ${styles.form}`}
        >
          <Field name="title" validate={isRequired}>
            {({ input, meta }) => (
              <div
                className={`${styles.input_wrap} ${
                  initialValues && styles.input_wrap_update
                }  ${
                  meta.error && meta.touched ? styles.form_input_error : ''
                }`}
              >
                <label>{t('adminPanel.form.hospitalTitleLabel')}</label>
                <input
                  {...input}
                  type="text"
                  placeholder={t('adminPanel.form.hospitalTitlePlaceholder')}
                />
                {meta.error && meta.touched && (
                  <span className={styles.error_message}>{t(meta.error)}</span>
                )}
              </div>
            )}
          </Field>
          <Field name="city" validate={isRequired}>
            {({ input, meta }) => (
              <div
                className={`${styles.input_wrap} ${
                  initialValues && styles.input_wrap_update
                } ${meta.error && meta.touched ? styles.form_input_error : ''}`}
              >
                <label>{t('adminPanel.form.cityLabel')}*</label>
                <input
                  {...input}
                  type="text"
                  placeholder={t('adminPanel.form.cityPlaceholder')}
                />
                {meta.error && meta.touched && (
                  <span className={styles.error_message}>{t(meta.error)}</span>
                )}
              </div>
            )}
          </Field>
          <Field name="street" validate={isRequired}>
            {({ input, meta }) => (
              <div
                className={`${styles.input_wrap} ${
                  initialValues && styles.input_wrap_update
                } ${meta.error && meta.touched ? styles.form_input_error : ''}`}
              >
                <label>{t('adminPanel.form.streetLabel')}*</label>
                <input
                  {...input}
                  type="text"
                  placeholder={t('adminPanel.form.streetPlaceholder')}
                />
                {meta.error && meta.touched && (
                  <span className={styles.error_message}>{t(meta.error)}</span>
                )}
              </div>
            )}
          </Field>
          <Field name="buildingNumber" validate={isRequired}>
            {({ input, meta }) => (
              <div
                className={`${styles.input_wrap} ${
                  initialValues && styles.input_wrap_update
                } ${meta.error && meta.touched ? styles.form_input_error : ''}`}
              >
                <label>{t('adminPanel.form.houseLabel')}*</label>
                <input
                  {...input}
                  type="text"
                  placeholder={t('adminPanel.form.housePlaceholder')}
                />
                {meta.error && meta.touched && (
                  <span className={styles.error_message}>{t(meta.error)}</span>
                )}
              </div>
            )}
          </Field>
          <Field name="mapPointUrl" validate={isRequired}>
            {({ input, meta }) => (
              <div
                className={`${styles.input_wrap} ${
                  initialValues && styles.input_wrap_update
                } ${meta.error && meta.touched ? styles.form_input_error : ''}`}
              >
                <label>{t('adminPanel.form.mapLocationLabel')}*</label>
                <input
                  {...input}
                  type="text"
                  placeholder={t('adminPanel.form.mapLocationPlaceholder')}
                />
                {meta.error && meta.touched && (
                  <span className={styles.error_message}>{t(meta.error)}</span>
                )}
              </div>
            )}
          </Field>
          <Field
            name="phoneNumber"
            validate={composeValidators(isRequired, isPhoneNumber)}
          >
            {({ input, meta }) => (
              <div
                className={`${styles.input_wrap} ${
                  initialValues && styles.input_wrap_update
                }  ${
                  meta.error && meta.touched ? styles.form_input_error : ''
                }`}
              >
                <label>{t('adminPanel.form.phoneLabel')}</label>
                <input
                  {...input}
                  type="tel"
                  value={values.phoneNumber}
                  onFocus={(e) => {
                    if (e.target.value === '') {
                      form.change('phoneNumber', '+');
                    }
                  }}
                  placeholder={t('adminPanel.form.phonePlaceholder')}
                />
                {meta.error && meta.touched && (
                  <span className={styles.error_message}>{t(meta.error)}</span>
                )}
              </div>
            )}
          </Field>
          <Field name="email" validate={composeValidators(isRequired, isEmail)}>
            {({ input, meta }) => (
              <div
                className={`${styles.input_wrap} ${
                  initialValues && styles.input_wrap_update
                } ${meta.error && meta.touched ? styles.form_input_error : ''}`}
              >
                <label>{t('adminPanel.form.emailLabel')}</label>
                <input
                  {...input}
                  type="text"
                  placeholder={t('adminPanel.form.emailPlaceholder')}
                />
                {meta.error && meta.touched && (
                  <span className={styles.error_message}>{t(meta.error)}</span>
                )}
              </div>
            )}
          </Field>
          <div
            className={`${styles.choose_time} ${
              initialValues && styles.choose_time_update
            }`}
          >
            <div className={styles.title}>{t('adminPanel.form.monFri')}</div>
            <div className={styles.content_wrap}>
              <TimePickerField
                name="monFriOpenAt"
                validate={isRequired}
                label={t('adminPanel.form.opening')}
              />
              <TimePickerField
                name="monFriCloseAt"
                validate={isRequired}
                label={t('adminPanel.form.closing')}
              />
            </div>
          </div>

          <div
            className={`${styles.choose_time} ${
              initialValues && styles.choose_time_update
            }`}
          >
            <div className={styles.title}>{t('adminPanel.form.sat')}</div>
            <div className={styles.content_wrap}>
              <TimePickerField
                name="satOpenAt"
                validate={!values.satHoliday ? isRequired : undefined}
                label={t('adminPanel.form.opening')}
                disabled={values.satHoliday as boolean}
              />
              <TimePickerField
                name="satCloseAt"
                validate={!values.satHoliday ? isRequired : undefined}
                label={t('adminPanel.form.closing')}
                disabled={values.satHoliday as boolean}
              />
              <div className={styles.checkbox_wrap}>
                <label>{t('adminPanel.form.weekend')}</label>
                <Field name="satHoliday" component="input" type="checkbox" />
              </div>
            </div>
          </div>
          <div
            className={`${styles.choose_time} ${
              initialValues && styles.choose_time_update
            }`}
          >
            <div className={styles.title}>{t('adminPanel.form.sun')}</div>
            <div className={styles.content_wrap}>
              <TimePickerField
                name="sunOpenAt"
                validate={!values.sunHoliday ? isRequired : undefined}
                label={t('adminPanel.form.opening')}
                disabled={values.sunHoliday as boolean}
              />
              <TimePickerField
                name="sunCloseAt"
                validate={!values.sunHoliday ? isRequired : undefined}
                label={t('adminPanel.form.closing')}
                disabled={values.sunHoliday as boolean}
              />
              <div className={styles.checkbox_wrap}>
                <label>{t('adminPanel.form.weekend')}</label>
                <Field
                  name="sunHoliday"
                  component="input"
                  type="checkbox"
                  defaultValue={true}
                />
              </div>
            </div>
          </div>

          <button
            type="submit"
            className={styles.form_button}
            disabled={pristine || submitting}
          >
            {t('buttons.save')}
          </button>
        </form>
      )}
    />
  );
};
